/**
 * msalConfig.ts — Entra (Azure AD) configuration for the backoffice SPA.
 *
 * The SPA performs MSAL login → acquires an access token scoped to the BFF,
 * which the BFF then validates for the `BackofficeUser` App Role on every `/api` request
 * (the role gate is server-side; see services/backoffice/app/auth.py). Config
 * comes from build-time Vite env (VITE_ENTRA_*); see vite-env.d.ts.
 *
 * Tokens are held in MSAL's in-memory cache (`cacheLocation: 'sessionStorage'`
 * for refresh continuity within a tab) — never hand-rolled into localStorage.
 */

import type { Configuration, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? '';
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? '';

/**
 * Falls back to the served origin for both an unset AND an empty-string env
 * value — the Dockerfile default is `ARG VITE_ENTRA_REDIRECT_URI=` (empty
 * string), which `??` alone does not catch (PR #490 shipped a blank redirect
 * URI to Entra as a result).
 */
export function resolveRedirectUri(envValue: string | undefined): string {
  return (
    envValue || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5174')
  );
}

const redirectUri = resolveRedirectUri(import.meta.env.VITE_ENTRA_REDIRECT_URI);

/** The BFF API scope requested for the access token (e.g. api://<id>/Admin.Access). */
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE ?? '';

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) {
          // eslint-disable-next-line no-console
          console.error('[msal]', message);
        }
      },
    },
  },
};

/** Scopes requested at interactive login (id + the BFF API scope when configured). */
export const loginRequest: RedirectRequest & PopupRequest = {
  scopes: apiScope ? [apiScope] : ['openid', 'profile'],
};

/** Scopes requested for the BFF access token (silent acquisition). */
export const apiTokenRequest = {
  scopes: apiScope ? [apiScope] : ['openid', 'profile'],
};
